import { useRequest } from 'features/request/useRequest';
import type { BimChapterEnum, BimPriceLevelEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import { useEffect, useMemo } from 'react';

import { bimWorkOrderPriceGql } from '../bimWorkOrderPriceQueries';

import type { ExtendedBimWorkOrderPriceData } from './helpers';
import { getWhereVariables, prepareCodeToSort } from './helpers';

export const useBimWorkOrderPrice = ({
    workOrderId,
    workOrderRootObjectId,
    chapter,
    includeRows = false,
    filterValues,
    priceLevel,
}: {
    chapter: BimChapterEnum | undefined;
    workOrderId: number;
    workOrderRootObjectId?: number;
    includeRows?: boolean;
    filterValues?: Record<string, string[]>;
    priceLevel: BimPriceLevelEnum | undefined;
}) => {
    const getPriceListDataVariables = useMemo(() => {
        if (chapter && workOrderId && workOrderRootObjectId && priceLevel) {
            return {
                workOrderId,
                workOrderRootObjectId,
                chapter,
                includeRows,
                priceLevel,
                where: getWhereVariables(filterValues),
            };
        }

        return undefined;
    }, [chapter, filterValues, includeRows, priceLevel, workOrderId, workOrderRootObjectId]);

    const [bimWorkOrderPriceListData, , , bimWorkOrderPriceListRequest] = useRequest({
        requestId: 'bimWorkOrderPriceList',
        query: bimWorkOrderPriceGql,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'bimWorkOrderPriceList',
        requestFn: bimWorkOrderPriceListRequest,
        requestVariables: getPriceListDataVariables,
    });

    useEffect(() => {
        const dataWithCodeNumber = bimWorkOrderPriceListData?.bimWorkOrderPrices?.items?.map((item) => ({
            ...item,
            code: prepareCodeToSort(item.completedValue.code ?? ''),
        }));

        const sortedDataWithCodeNumber = dataWithCodeNumber?.sort(
            (prev: ExtendedBimWorkOrderPriceData, next: ExtendedBimWorkOrderPriceData) => prev.code - next.code,
        );

        const sortedDataLsr = sortedDataWithCodeNumber?.map((item) => {
            const { code, ...rest } = item;

            return rest;
        });

        if (sortedDataLsr && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                sortedDataLsr,
                bimWorkOrderPriceListData.bimWorkOrderPrices?.totalCount,
            );
        }
    }, [
        getRowParams,
        bimWorkOrderPriceListData?.bimWorkOrderPrices?.totalCount,
        bimWorkOrderPriceListData?.bimWorkOrderPrices?.items,
    ]);

    const priceListCounter = useMemo(
        () => bimWorkOrderPriceListData?.bimWorkOrderPrices?.totalCount ?? 0,
        [bimWorkOrderPriceListData?.bimWorkOrderPrices?.totalCount],
    );

    const fetchedItemsCounter = useMemo(() => {
        if (!getRowParams?.current?.endRow || !priceListCounter) {
            return 0;
        }

        if (getRowParams?.current?.endRow > priceListCounter) {
            return priceListCounter;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [priceListCounter, getRowParams?.current?.endRow]);

    return {
        bimPriceListDataSource: dataSource,
        priceListCounter,
        fetchedItemsCounter,
    };
};
